import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useUIStore } from '@store/UIStore';

import { REPORT_SCROLL_ID, SCROLL_TIMEOUT } from '@common/configs';

import type { IUseSidebar } from './types';

export const useMobileView = () => {
  const showMobileSidebar = useUIStore((state) => {
    return state.showMobileSidebar;
  });
  const setShowMobileSidebar = useUIStore((state) => {
    return state.setShowMobileSidebar;
  });

  const closeMobileSidebar = useCallback(() => {
    setShowMobileSidebar(true);
  }, [setShowMobileSidebar]);

  const openMobileSidebar = useCallback(() => {
    setShowMobileSidebar(false);
  }, [setShowMobileSidebar]);

  return { showMobileSidebar, closeMobileSidebar, openMobileSidebar };
};

export const useSidebar = ({ activeId, selectedId, setSelectedId, slideRefs, isMobile }: IUseSidebar) => {
  const [indicator, setIndicator] = useState({ top: 0, height: 60 });
  const [isClickActive, setIsClickActive] = useState(false);
  const clickTimeoutRef = useRef<number | null>(null);
  const setShowMobileSidebar = useUIStore((state) => {
    return state.setShowMobileSidebar;
  });

  // Logic for the indicator position
  const updateIndicator = useCallback((id: string, itemRefs: RefObject<Record<string, HTMLLIElement | null>>) => {
    const activeItem = itemRefs.current[id];
    if (activeItem) {
      setIndicator({
        top: activeItem.offsetTop,
        height: activeItem.offsetHeight,
      });
    }
  }, []);

  // Effect for scroll detection delay
  useEffect(() => {
    if (!selectedId) {
      return;
    }
    const timeout = setTimeout(() => {
      return setIsClickActive(false);
    }, SCROLL_TIMEOUT);
    return () => {
      return clearTimeout(timeout);
    };
  }, [activeId, selectedId]);

  const onItemClick = (sectionId: string) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    setIsClickActive(true);

    if (isMobile) {
      setShowMobileSidebar(false);
    }

    const targetId = [...slideRefs.keys()].find((id) => {
      return id.startsWith(sectionId);
    });
    const element = targetId ? slideRefs.get(targetId)?.current : null;
    const container = element?.closest(`[data-scroll-target="${REPORT_SCROLL_ID}"]`);

    if (element && container) {
      setSelectedId(sectionId);
      container.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth',
      });
      clickTimeoutRef.current = setTimeout(() => {
        setIsClickActive(false);
        clickTimeoutRef.current = null;
      }, SCROLL_TIMEOUT);
    } else {
      setIsClickActive(false);
    }
  };

  const onHandleClick = (sectionId: string) => {
    if (isMobile) {
      setTimeout(() => {
        return onItemClick(sectionId);
      }, 300);
    } else {
      onItemClick(sectionId);
    }
  };

  return {
    indicator,
    isClickActive,
    onHandleClick,
    updateIndicator,
  };
};
